import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { adminCreateProduct, fetchAdminProductCategories, uploadFile } from "@/lib/api";
import { Loader2, Upload, X, Package, Search } from "lucide-react";
import { toast } from "sonner";

const Field = ({ label, children, hint }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</Label>
    <div className="mt-1">{children}</div>
    {hint && <div className="text-[11px] text-zinc-400 mt-1">{hint}</div>}
  </div>
);

/** Inline (no pop-up) form that creates a Products row in Airtable. */
export default function NewProductPanel({ callerUserRecordId, products = [], onCreated, onClose }) {
  const [form, setForm] = useState({
    post_title: "", price: "", inventory: 100, weight: "", excerpt: "", blurb: "", post_content: "",
    mi_image_url: "", video_url: "", orevital: true, mindful_minerals: false, featured: false, active: true,
  });
  const [cats, setCats] = useState([]);
  const [catIds, setCatIds] = useState([]);
  const [isBundle, setIsBundle] = useState(false);
  const [bundleIds, setBundleIds] = useState([]);
  const [bundleQ, setBundleQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    fetchAdminProductCategories(callerUserRecordId).then((d) => setCats(d?.items || [])).catch(() => {});
  }, [callerUserRecordId]);

  async function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadFile(file, callerUserRecordId, "product");
      const url = res?.url || res?.file?.url || "";
      if (!url) throw new Error("No URL returned");
      set("mi_image_url", url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function save() {
    if (!form.post_title.trim()) return toast.error("Give the product a name");
    if (!(Number(form.price) > 0)) return toast.error("Enter an MSRP greater than 0");
    if (isBundle && bundleIds.length < 2) return toast.error("Pick at least 2 products for a bundle");
    setSaving(true);
    try {
      const res = await adminCreateProduct({
        callerUserRecordId,
        ...form,
        price: Number(form.price),
        inventory: Number(form.inventory || 0),
        weight: Number(form.weight || 0),
        category_ids: catIds,
        bundle_item_ids: isBundle ? bundleIds : [],
      });
      toast.success(`${form.post_title.trim()} created · /product/${res?.slug || ""}`);
      onCreated?.(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create product");
    } finally {
      setSaving(false);
    }
  }

  const bundleCandidates = products
    .filter((p) => p.active && !p.is_bundle)
    .filter((p) => !bundleQ || p.name.toLowerCase().includes(bundleQ.toLowerCase()))
    .slice(0, 12);

  return (
    <div className="rounded-2xl bg-white border border-zinc-200 p-4 sm:p-5 mb-4 space-y-4" data-testid="new-product-panel">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-bold text-zinc-900 flex items-center gap-2"><Package size={16} className="text-violet-600" /> New product</h2>
        <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-700" data-testid="new-product-close"><X size={16} /></button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Product name">
          <Input value={form.post_title} onChange={(e) => set("post_title", e.target.value)} placeholder="e.g. Fresh Face Ritual" className="h-10" data-testid="new-product-name" />
        </Field>
        <div className="grid grid-cols-3 gap-2">
          <Field label="MSRP ($)"><Input type="number" min="0" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} className="h-10" data-testid="new-product-price" /></Field>
          <Field label="Inventory"><Input type="number" min="0" value={form.inventory} onChange={(e) => set("inventory", e.target.value)} className="h-10" data-testid="new-product-inventory" /></Field>
          <Field label="Weight (oz)"><Input type="number" min="0" step="0.1" value={form.weight} onChange={(e) => set("weight", e.target.value)} className="h-10" data-testid="new-product-weight" /></Field>
        </div>
      </div>

      <Field label="Excerpt" hint="Short tagline under the title. Use **bold** for a heading line.">
        <textarea value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} rows={2} className="w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm" data-testid="new-product-excerpt" />
      </Field>
      <Field label="Blurb" hint={isBundle ? "Auto-filled with “Bundle includes: …” from the products you pick." : "One-line call-out shown under the excerpt."}>
        <Input value={form.blurb} onChange={(e) => set("blurb", e.target.value)} className="h-10" data-testid="new-product-blurb" />
      </Field>
      <Field label="Description (HTML or text)">
        <textarea value={form.post_content} onChange={(e) => set("post_content", e.target.value)} rows={6} className="w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm font-mono" data-testid="new-product-description" />
      </Field>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Main image">
          <div className="flex items-center gap-3">
            {form.mi_image_url ? <img src={form.mi_image_url} alt="" className="h-14 w-14 rounded-xl object-cover border border-zinc-200" /> : <div className="h-14 w-14 rounded-xl bg-zinc-100 border border-zinc-200" />}
            <div className="flex-1 space-y-1.5">
              <Input value={form.mi_image_url} onChange={(e) => set("mi_image_url", e.target.value)} placeholder="Paste image URL…" className="h-9 text-xs" data-testid="new-product-image-url" />
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="new-product-image-file" />
              <Button type="button" variant="outline" size="sm" className="rounded-lg text-xs h-8" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="new-product-upload-btn">
                {uploading ? <Loader2 size={12} className="animate-spin mr-1" /> : <Upload size={12} className="mr-1" />} Upload
              </Button>
            </div>
          </div>
        </Field>
        <Field label="Video URL (optional)">
          <Input value={form.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="YouTube / mp4 link" className="h-10" data-testid="new-product-video" />
        </Field>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Brand & flags">
          <div className="flex flex-wrap gap-4 text-sm">
            {[["orevital", "OreVital"], ["mindful_minerals", "Mindful Minerals"], ["featured", "Featured"], ["active", "Active"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2"><Switch checked={!!form[k]} onCheckedChange={(v) => set(k, v)} data-testid={`new-product-${k}`} /> {l}</label>
            ))}
          </div>
        </Field>
        <Field label="Categories">
          <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto" data-testid="new-product-categories">
            {cats.map((c) => {
              const on = catIds.includes(c.id);
              return (
                <button key={c.id} type="button" onClick={() => setCatIds((s) => (on ? s.filter((x) => x !== c.id) : [...s, c.id]))}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition ${on ? "bg-violet-600 border-violet-600 text-white" : "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
                  data-testid={`new-product-cat-${c.id}`}>{c.name}</button>
              );
            })}
          </div>
        </Field>
      </div>

      <div className="rounded-xl border border-zinc-100 bg-zinc-50/60 p-3 space-y-2">
        <label className="flex items-center gap-2 text-sm font-semibold"><Switch checked={isBundle} onCheckedChange={setIsBundle} data-testid="new-product-is-bundle" /> This is a bundle</label>
        {isBundle && (
          <>
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input value={bundleQ} onChange={(e) => setBundleQ(e.target.value)} placeholder="Search products to include…" className="h-9 pl-8 text-xs" data-testid="new-product-bundle-search" />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {bundleCandidates.map((p) => {
                const on = bundleIds.includes(p.id);
                return (
                  <button key={p.id} type="button" onClick={() => setBundleIds((s) => (on ? s.filter((x) => x !== p.id) : [...s, p.id]))}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${on ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-zinc-200 text-zinc-700"}`}
                    data-testid={`new-product-bundle-${p.id}`}>{p.name}</button>
                );
              })}
            </div>
            {bundleIds.length > 0 && <div className="text-[11px] text-zinc-500">{bundleIds.length} selected</div>}
          </>
        )}
      </div>

      <div className="text-[11px] text-zinc-500">
        On save: slug, post ID and <span className="font-mono">seo_url_Orevital</span> are generated, and a Visible pricing row per role is created at MSRP (edit role prices in the product panel afterwards).
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" className="rounded-xl" onClick={onClose} data-testid="new-product-cancel">Cancel</Button>
        <Button onClick={save} disabled={saving} className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold" data-testid="new-product-save">
          {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Create product
        </Button>
      </div>
    </div>
  );
}
